import {
  handleCompanionSubscribe,
  handleTreatPackCheckout,
  instantiateCustomerPortal,
} from "@api/Billing";
import { AccountItemBlock } from "@components/account/AccountItem";
import {
  DSButton,
  DSCardWrapper,
} from "@components/design-system/molecules";
import {
  faBone,
  faChevronDown,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useCompanionSubscription,
  useRespondToTreatPurchaseComplete,
  useTreatBalance,
  useTreatDailySummaries,
} from "@hooks";
import {
  COMPANION_LARGE_PRICE_ID_MONTHLY,
  COMPANION_SMALL_PRICE_ID_MONTHLY,
  COMPANION_SNACK_PACK_PRICE_ID,
} from "@src/constants";
import { useState } from "react";

const formatDate = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const formatNet = (n) => {
  if (n > 0) return `+${n.toLocaleString()}`;
  return n.toLocaleString();
};

const FAQS = [
  {
    q: "Do unused Treats roll over?",
    a: "Treats included in your monthly plan don't carry over — they reset each month. Treats you buy via a Treat Pack never expire.",
  },
  {
    q: "What happens when I run out?",
    a: "You'll see a friendly message letting you know you're out, with the option to buy a Treat Pack or upgrade your plan. No surprise charges — it's a hard stop.",
  },
  {
    q: "Will I get a warning before I run out?",
    a: "Yes — you'll get a notification when you're down to 100 Treats.",
  },
  {
    q: "Do different Companions cost different amounts?",
    a: "Yes. A record summarization processes lengthy documents and typically costs more Treats than a quick edit or a Q&A. Pricing reflects the actual work being done.",
  },
  {
    q: "What about my existing Scribe plan?",
    a: "Nothing changes. Your Scribe plan stays exactly as it is. Companion is priced and sold separately.",
  },
];

const FaqItem = ({
  question,
  answer,
  isOpen,
  onToggle,
  dataTestId,
}) => (
  <DSCardWrapper
    interactive
    onClick={onToggle}
    aria-label={`${isOpen ? "Collapse" : "Expand"} ${question}`}
    dataTestId={dataTestId}
  >
    <div className="flex flex-col gap-xs">
      <div className="flex flex-row items-center justify-between gap-md">
        <span className="text-sm font-semibold text-primary">
          {question}
        </span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`text-secondary transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>
      {isOpen && <p className="text-sm text-secondary">{answer}</p>}
    </div>
  </DSCardWrapper>
);

const TreatUsageBar = ({ used, total }) => {
  const safeTotal = total > 0 ? total : 0;
  const percent =
    safeTotal === 0
      ? 0
      : Math.max(0, Math.min(100, (used / safeTotal) * 100));
  return (
    <div
      className="w-full h-2 bg-secondary rounded-full overflow-hidden"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={safeTotal}
      aria-valuenow={used}
      data-testid="companion-treat-usage-bar"
    >
      <div
        className="h-full bg-brand transition-all"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
};

const TREAT_PACK = {
  key: "pack",
  name: "Snack Pack",
  priceDollars: 10,
  cadence: "one-time",
  treats: 500,
  treatsLabel: "500 Treats",
  description: "",
  priceId: COMPANION_SNACK_PACK_PRICE_ID,
};

const MONTHLY_PLANS = [
  {
    key: "munchkin",
    name: "Munchkin",
    priceDollars: 35,
    cadence: "/mo",
    treats: 2000,
    treatsLabel: "2,000 Treats / month",
    description:
      "For vets using Companions as part of their daily workflow.",
    priceId: COMPANION_SMALL_PRICE_ID_MONTHLY,
  },
  {
    key: "chonk",
    name: "Chonk",
    priceDollars: 150,
    cadence: "/mo",
    treats: 10000,
    treatsLabel: "10,000 Treats / month",
    description:
      "For power users and clinics with heavy Companion workflows.",
    priceId: COMPANION_LARGE_PRICE_ID_MONTHLY,
  },
];

const PricingCard = ({
  name,
  priceDollars,
  cadence,
  treatsLabel,
  description,
  ctaLabel,
  onCta,
  ctaDisabled,
  dataTestId,
  ctaTestId,
  variant = "info",
}) => (
  <DSCardWrapper variant={variant} dataTestId={dataTestId}>
    <div className="flex flex-col gap-xs">
      <div className="flex flex-row items-baseline justify-between gap-sm">
        <h5 className="text-primary">{name}</h5>
        <span className="text-sm text-secondary">
          ${priceDollars}
          <span className="text-xs"> {cadence}</span>
        </span>
      </div>
      <div className="text-sm text-primary">{treatsLabel}</div>
      {description && (
        <p className="text-xs text-secondary">{description}</p>
      )}
      <div className="mt-sm">
        <DSButton
          variant="secondary"
          disabled={ctaDisabled}
          onClick={onCta}
          dataTestId={ctaTestId}
        >
          {ctaLabel}
        </DSButton>
      </div>
    </div>
  </DSCardWrapper>
);

function DailyActivity({ summaries, loading }) {
  if (loading && summaries.length === 0) {
    return (
      <div className="py-md text-secondary text-sm">
        Loading daily activity…
      </div>
    );
  }

  if (summaries.length === 0) {
    return (
      <div
        className="py-md text-secondary text-sm"
        data-testid="companion-daily-activity-empty"
      >
        No activity in the last 30 days.
      </div>
    );
  }

  return (
    <div
      className="flex flex-col"
      data-testid="companion-daily-activity"
    >
      <div className="grid grid-cols-4 gap-sm text-xs text-secondary py-xs border-b border-secondary">
        <span>Date</span>
        <span className="text-right">Credited</span>
        <span className="text-right">Used</span>
        <span className="text-right">Net</span>
      </div>
      {summaries.map((s) => (
        <div
          key={s.date}
          className="grid grid-cols-4 gap-sm text-sm py-xs border-b border-secondary last:border-0"
        >
          <span className="text-primary">{formatDate(s.date)}</span>
          <span className="text-right text-success">
            {s.treatsCredited > 0
              ? `+${s.treatsCredited.toLocaleString()}`
              : "—"}
          </span>
          <span className="text-right text-danger">
            {s.treatsUsed > 0
              ? `-${s.treatsUsed.toLocaleString()}`
              : "—"}
          </span>
          <span
            className={
              s.netChange >= 0
                ? "text-right text-primary"
                : "text-right text-danger"
            }
          >
            {formatNet(s.netChange)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function CompanionBillingView() {
  const { balance, loading, error, refetch } = useTreatBalance();
  const {
    summaries,
    loading: summariesLoading,
    refetch: refetchSummaries,
  } = useTreatDailySummaries({ days: 30 });
  const [purchasingPriceId, setPurchasingPriceId] = useState(null);
  const [subscribingKey, setSubscribingKey] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const {
    subscription,
    isActive: hasActiveSubscription,
    isCanceledButActive,
    isTeamsCustomer,
  } = useCompanionSubscription();

  useRespondToTreatPurchaseComplete({
    refetchBalance: refetch,
    refetchDailySummaries: refetchSummaries,
  });

  const onPurchase = (priceId) => {
    if (!priceId) return;
    setPurchasingPriceId(priceId);
    handleTreatPackCheckout({
      priceId,
      route: `${window.location.pathname}${window.location.search}`,
      onError: () => setPurchasingPriceId(null),
    });
  };

  const onSubscribe = (plan) => {
    if (!plan.priceId) return;
    setSubscribingKey(plan.key);
    handleCompanionSubscribe({
      priceId: plan.priceId,
      route: `${window.location.pathname}${window.location.search}`,
      onError: () => setSubscribingKey(null),
    });
  };

  if (loading && !balance) {
    return (
      <div className="py-md text-secondary text-sm">
        Loading treat balance…
      </div>
    );
  }

  if (error && !balance) {
    return (
      <div className="py-md flex flex-col gap-sm">
        <p className="text-danger text-sm">
          Couldn't load treat balance.
        </p>
        <DSButton
          onClick={() => refetch()}
          dataTestId="companion-settings-retry"
        >
          Retry
        </DSButton>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-lg">
      <AccountItemBlock
        title="Treat usage"
        desc="Treats power your Companions — each action deducts a variable amount."
      >
        <div className="flex flex-col gap-sm">
          <div className="flex items-center gap-sm">
            <FontAwesomeIcon
              icon={faBone}
              className="text-brand"
              size="xl"
            />
            <span
              className="text-2xl font-semibold text-primary"
              data-testid="companion-treat-balance"
            >
              {balance ? balance.totalBalance.toLocaleString() : "—"}{" "}
              Treats remaining
            </span>
          </div>
          {balance && (
            <div className="flex flex-col gap-xs">
              {hasActiveSubscription &&
                subscription.monthlyTreats > 0 && (
                  <>
                    <TreatUsageBar
                      used={
                        subscription.monthlyTreats -
                        balance.monthlyBalance
                      }
                      total={subscription.monthlyTreats}
                    />
                    <div className="text-xs text-secondary">
                      {balance.monthlyBalance.toLocaleString()} of{" "}
                      {subscription.monthlyTreats.toLocaleString()}{" "}
                      monthly Treats remaining
                      {balance.nextResetAt && (
                        <>
                          {" "}
                          · Resets{" "}
                          {new Date(
                            balance.nextResetAt,
                          ).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                          })}
                        </>
                      )}
                    </div>
                  </>
                )}
              {balance.permanentBalance > 0 && (
                <div className="text-xs text-secondary">
                  {balance.permanentBalance.toLocaleString()}{" "}
                  permanent Treats remaining
                </div>
              )}
            </div>
          )}
        </div>
      </AccountItemBlock>

      <AccountItemBlock
        title="Treat Pack"
        desc="Treats purchased through packs never expire. Top up anytime, with or without a monthly plan."
      >
        <PricingCard
          name={TREAT_PACK.name}
          priceDollars={TREAT_PACK.priceDollars}
          cadence={TREAT_PACK.cadence}
          treatsLabel={TREAT_PACK.treatsLabel}
          description={TREAT_PACK.description}
          ctaLabel={
            !TREAT_PACK.priceId
              ? "Coming soon"
              : purchasingPriceId === TREAT_PACK.priceId
                ? "Redirecting…"
                : "Purchase Treat Pack"
          }
          onCta={() => onPurchase(TREAT_PACK.priceId)}
          ctaDisabled={
            !TREAT_PACK.priceId || purchasingPriceId !== null
          }
          dataTestId="companion-treat-pack-card"
          ctaTestId={`buy-treats-${TREAT_PACK.key}`}
        />
      </AccountItemBlock>

      <AccountItemBlock
        title="Treat Subscriptions"
        desc="You'll get fresh Treats for your Companions each month. These don't roll over into the next month, so make sure to use them up!"
      >
        {isTeamsCustomer ? (
          <p className="text-secondary text-xs md:text-sm">
            You are currently using a paid team account which is
            managed by someone else. Please contact them if you need
            to make any changes to your subscription.
          </p>
        ) : (
          <div className="flex flex-col gap-sm">
            {MONTHLY_PLANS.map((plan) => {
              const isActive =
                hasActiveSubscription &&
                subscription.planKey === plan.key;
              const isCanceling = isActive && isCanceledButActive;
              const endsAt =
                isCanceling && subscription.activeUntil
                  ? new Date(
                      subscription.activeUntil,
                    ).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })
                  : null;
              const isSubscribing = subscribingKey === plan.key;
              const disabled =
                isActive ||
                hasActiveSubscription ||
                !plan.priceId ||
                subscribingKey !== null ||
                purchasingPriceId !== null;
              const getCtaLabel = () => {
                if (isCanceling) return `Canceled · ends ${endsAt}`;
                if (isActive) return "Current plan";
                if (!plan.priceId) return "Coming soon";
                if (isSubscribing) return "Redirecting…";
                return "Subscribe";
              };
              const getVariant = () => {
                if (isCanceling) return "warning";
                if (isActive) return "success";
                return "info";
              };
              return (
                <PricingCard
                  key={plan.key}
                  name={plan.name}
                  priceDollars={plan.priceDollars}
                  cadence={plan.cadence}
                  treatsLabel={plan.treatsLabel}
                  description={plan.description}
                  variant={getVariant()}
                  ctaLabel={getCtaLabel()}
                  onCta={() => onSubscribe(plan)}
                  ctaDisabled={disabled}
                  dataTestId={`companion-plan-${plan.key}`}
                  ctaTestId={`subscribe-plan-${plan.key}`}
                />
              );
            })}
            {hasActiveSubscription && (
              <DSButton
                variant="secondary"
                onClick={instantiateCustomerPortal}
                dataTestId="companion-manage-subscription"
              >
                Manage subscription
              </DSButton>
            )}
          </div>
        )}
      </AccountItemBlock>

      <AccountItemBlock
        title="FAQs"
        desc="Common questions about Treats and Companion pricing."
      >
        <div
          className="flex flex-col gap-sm"
          data-testid="companion-billing-faqs"
        >
          {FAQS.map((faq, i) => (
            <FaqItem
              key={faq.q}
              question={faq.q}
              answer={faq.a}
              isOpen={openFaq === i}
              onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              dataTestId={`companion-faq-${i}`}
            />
          ))}
        </div>
      </AccountItemBlock>

      <AccountItemBlock
        title="Daily activity"
        desc="Treats earned and spent over the last 30 days."
      >
        <DailyActivity
          summaries={summaries}
          loading={summariesLoading}
        />
      </AccountItemBlock>
    </div>
  );
}
